import { useEffect, useState } from 'react'
import { useTableStore } from '@/store/table/table'
import { useAppTheme } from '@/theme/ThemeContext'
import { getFilterOptions } from '@/services/consumerService'
import AdditionalFiltersDialog, {
  type AdditionalFilterField,
  type AdditionalFilterValue,
} from '@/components/common/AdditionalFiltersDialog'

// A date range is two filters with operators. The text fields here cannot hold it, so the
// column's own filter owns it and this dialog leaves it alone.
const hasDateRange = (column: { filterSpec?: { kind: string } }) =>
  column.filterSpec?.kind === 'date'

const FilterModal = ({
  open,
  handleClose,
}: {
  open: boolean
  handleClose: () => void
}) => {
  const {
    columnsStore,
    apiFilters,
    setApiFilters,
    clearApiFilters,
  } = useTableStore()
  const { langKey } = useAppTheme()
  const [values, setValues] = useState<Record<string, AdditionalFilterValue>>({})
  const [fieldOptions, setFieldOptions] = useState<Record<string, string[]>>({})
  const [loadingField, setLoadingField] = useState<string | null>(null)
  const dateRangeFields = new Set(
    (columnsStore ?? []).filter(hasDateRange).map(column => column.field)
  )
  const dateRangeFilters = apiFilters.filter(filter => dateRangeFields.has(filter.name))

  useEffect(() => {
    const initialValues: Record<string, AdditionalFilterValue> = {}
    apiFilters.forEach(filter => {
      if (filter.value?.[0] && !dateRangeFields.has(filter.name)) {
        initialValues[filter.name] = filter.value[0]
      }
    })
    setValues(initialValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilters, open, columnsStore])

  const handleOpenField = async (field: string) => {
    if (fieldOptions[field]) return

    setLoadingField(field)
    try {
      const accountId = localStorage.getItem('accountId')
      if (!accountId) return

      const options = await getFilterOptions(accountId, field, langKey)
      setFieldOptions(previous => ({ ...previous, [field]: options.map(String) }))
    } catch (error) {
      console.error(`Error fetching options for ${field}:`, error)
    } finally {
      setLoadingField(null)
    }
  }

  const fields: AdditionalFilterField[] = (columnsStore ?? [])
    .filter(column => !hasDateRange(column))
    .filter(column =>
      column.filterSpec
        ? column.filterSpec.kind !== 'none'
        : column.filter && column.filter.type !== 'none'
    )
    .map(column => ({
      name: column.field,
      label: column.headerName,
      type: 'autocomplete',
      options: fieldOptions[column.field] ?? [],
      loading: loadingField === column.field,
      onOpen: () => handleOpenField(column.field),
    }))

  const handleApply = () => {
    const nextFilters = Object.entries(values)
      .filter(([, value]) => typeof value === 'string' && value.trim())
      .map(([name, value]) => ({ name, value: [String(value)] }))

    setApiFilters([...nextFilters, ...dateRangeFilters])
    handleClose()
  }

  const handleClear = () => {
    setValues({})
    clearApiFilters()
  }

  return (
    <AdditionalFiltersDialog
      open={open}
      fields={fields}
      values={values}
      onChange={(name, value) => {
        setValues(previous => ({ ...previous, [name]: value }))
      }}
      onApply={handleApply}
      onClear={handleClear}
      onClose={handleClose}
    />
  )
}

export default FilterModal
